/**
 * 3D background configuration.
 * Three.js-powered 3D objects with rotation, camera, color, and text controls.
 * Lazy-loaded — zero cost when not used.
 */

import { colors } from './colors';

// ─── Shape definitions ─────────────────────────────────────────────

export const SHAPES = [
  { id: 'cube',        name: 'Cube',        icon: '▣' },
  { id: 'sphere',      name: 'Sphere',      icon: '●' },
  { id: 'torus',       name: 'Torus',       icon: '◎' },
  { id: 'torusKnot',   name: 'Knot',        icon: '∞' },
  { id: 'icosahedron', name: 'Icosa',       icon: '⬡' },
  { id: 'wireframe',   name: 'Wireframe',   icon: '◌' },
  { id: 'mobius',       name: 'Möbius',      icon: '∿' },
  { id: 'parametric',  name: 'Parametric',  icon: '≈' },
];

// Base sizes per shape (at scale multiplier 1.0)
export const SHAPE_BASE_SIZES = {
  cube: 2.0,
  sphere: 1.6,
  torus: 1.3,
  torusKnot: 1.1,
  icosahedron: 1.6,
  wireframe: 1.6,
  mobius: 1.5,
  parametric: 2.2,
};

// ─── Scale levels (universal for all shapes) ───────────────────────

export const SHAPE_SCALE_LEVELS = [
  { label: 'XS',     mult: 0.5 },
  { label: 'Small',  mult: 0.7 },
  { label: 'Medium', mult: 1.0 },
  { label: 'Large',  mult: 1.3 },
  { label: 'XL',     mult: 1.7 },
];

// ─── Per-shape parameter options ────────────────────────────────────

export const SPHERE_DETAIL_LEVELS = [
  { label: 'Low',      segments: 8 },
  { label: 'Medium',   segments: 16 },
  { label: 'High',     segments: 32 },
  { label: 'Ultra',    segments: 48 },
];

export const TORUS_TUBE_LEVELS = [
  { label: 'Thin',     ratio: 0.15 },
  { label: 'Medium',   ratio: 0.3 },
  { label: 'Thick',    ratio: 0.45 },
  { label: 'Fat',      ratio: 0.6 },
];

export const ICOSA_DETAIL_LEVELS = [
  { label: 'Faceted',  detail: 0 },
  { label: 'Low',      detail: 1 },
  { label: 'Medium',   detail: 2 },
  { label: 'Smooth',   detail: 4 },
];

export const PARAMETRIC_TYPES = [
  { id: 'wave',    name: 'Wave' },
  { id: 'ripple',  name: 'Ripple' },
  { id: 'twist',   name: 'Twist' },
  { id: 'saddle',  name: 'Saddle' },
];

// ─── FOV / focal length levels ──────────────────────────────────────

export const P3D_FOV_LEVELS = [
  { label: 'Wide',      fov: 75 },
  { label: 'Standard',  fov: 55 },
  { label: 'Medium',    fov: 45 },
  { label: 'Portrait',  fov: 35 },
  { label: 'Telephoto', fov: 25 },
];

// ─── Face color presets — sourced from color foundation ──────────────

export const P3D_FACE_COLORS = [
  { id: 'glass',   name: 'Glass',   hex: colors.white },
  { id: 'smoke',   name: 'Smoke',   hex: '#1a1a2e' },
  { id: 'dark',    name: 'Dark',    hex: colors.black },
  { id: 'emerald', name: 'Emerald', hex: colors.emerald[400] },
  { id: 'blue',    name: 'Blue',    hex: colors.blue[400] },
  { id: 'violet',  name: 'Violet',  hex: colors.violet[400] },
  { id: 'rose',    name: 'Rose',    hex: colors.rose[400] },
  { id: 'gold',    name: 'Gold',    hex: colors.amber[400] },
  { id: 'teal',    name: 'Teal',    hex: colors.teal[400] },
];

// ─── Edge color presets — sourced from color foundation ──────────────

export const P3D_EDGE_COLORS = [
  { id: 'white',   name: 'White',   hex: colors.white },
  { id: 'gray',    name: 'Gray',    hex: colors.gray[500] },
  { id: 'emerald', name: 'Emerald', hex: colors.emerald[400] },
  { id: 'blue',    name: 'Blue',    hex: colors.blue[400] },
  { id: 'violet',  name: 'Violet',  hex: colors.violet[400] },
  { id: 'rose',    name: 'Rose',    hex: colors.rose[400] },
  { id: 'gold',    name: 'Gold',    hex: colors.amber[400] },
  { id: 'none',    name: 'Hidden',  hex: colors.black },
];

// ─── Text color presets — sourced from color foundation ──────────────

export const P3D_TEXT_COLORS = [
  { id: 'white',   name: 'White',   hex: colors.white },
  { id: 'gray',    name: 'Gray',    hex: colors.gray[400] },
  { id: 'black',   name: 'Black',   hex: colors.black },
  { id: 'emerald', name: 'Emerald', hex: colors.emerald[400] },
  { id: 'blue',    name: 'Blue',    hex: colors.blue[400] },
  { id: 'violet',  name: 'Violet',  hex: colors.violet[400] },
  { id: 'rose',    name: 'Rose',    hex: colors.rose[400] },
  { id: 'gold',    name: 'Gold',    hex: colors.amber[400] },
];

// ─── Face labels (cube only) ────────────────────────────────────────

export const FACE_LABELS = ['Front', 'Back', 'Right', 'Left', 'Top', 'Bottom'];

// ─── Default config ─────────────────────────────────────────────────

export const DEFAULT_PRIMITIVES3D = {
  enabled: false,
  shape: 'cube',

  // Global: rotation
  rotateX: 0,
  rotateY: 45,
  rotateZ: 0,

  // Global: object position
  posX: 0,
  posY: 0.5,

  // Global: camera
  fov: 1,            // index → P3D_FOV_LEVELS[1] = Standard (55°)
  camDist: 5.0,
  camX: 0,
  camY: 2.0,

  // Global: colors
  bgColor: 'ocean',
  faceColor: 'rose',
  edgeColor: 'white',
  textColor: 'white',
  faceOpacity: 10,
  edgeOpacity: 25,

  // Global: misc
  showGrid: true,
  darken: 0,
  shapeScale: 2,     // index → SHAPE_SCALE_LEVELS[2] = Medium

  // Cube-specific
  faces: ['', '', '', '', '', ''],

  // Sphere-specific
  sphereDetail: 2,   // index → SPHERE_DETAIL_LEVELS

  // Torus-specific
  torusTube: 1,      // index → TORUS_TUBE_LEVELS

  // Torus Knot-specific
  knotP: 2,
  knotQ: 3,

  // Icosahedron-specific
  icosaDetail: 0,    // index → ICOSA_DETAIL_LEVELS

  // Wireframe-specific
  wireDetail: 2,     // index → SPHERE_DETAIL_LEVELS (reuses)

  // Möbius-specific
  mobiusWidth: 1,    // 0-3 index mapping to width ratio
  mobiusTwists: 1,   // 1, 2, or 3 half-twists

  // Parametric-specific
  parametricType: 'wave',
  parametricAmplitude: 2, // 0-4 index
};
